import { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import { useReactFlow, getOutgoers } from "reactflow";
import MuiListItemIcon from "@mui/material/ListItemIcon";
import { ValueInputWrapper } from "../StyledComponents";
import IconButton from "@mui/material/IconButton";
import { ThemedTrashIcon } from "assets/CXFIcons/Triggers";
import Tooltip from "@mui/material/Tooltip";
import AutocompleteInput from "../AutocompleteInput";
import { ComponentPropsContext, ComponentPropsContextApi } from "layouts/Flow Designer/context";
import { THEME_COLORS } from "constants/theme";
import { ConditionalWrapper, tryCatchWrapper } from "helpers/common";
import useFitView from "hooks/useFitView";
import { EDGE_TYPES } from "constants/flowDesigner";
import { useDebouncer } from "hooks/useDebouncer";
import Checkbox from "@mui/material/Checkbox";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

const ListItemIcon = styled(MuiListItemIcon)(({ theme, disabled }) => {
   return {
      opacity: disabled ? 0.3 : 1,
      "& svg": {
         color: disabled ? theme.palette.grey[500] : THEME_COLORS[theme.palette?.mode?.toUpperCase()].PRIMARY_FONT,

         "&:hover": {
            color: disabled ? `${theme.palette.grey[500]} !important` : "#bd0000",
            cursor: disabled ? "default !important" : "pointer"
         }
      }
   };
});

const ValueInput = ({ field = {}, name, isBranch, allowEditWorkflow, setRemoveBranchData, index }) => {
   const { actionForm } = useContext(ComponentPropsContext);
   const { setActionForm } = useContext(ComponentPropsContextApi);
   const { getNode, getNodes, getEdges, deleteElements, setNodes } = useReactFlow();
   const { handleFitView } = useFitView();
   let { value, id, key, label } = field;
   const { debouncer } = useDebouncer(setValue);
   const [firstValue, setFirstValue] = useState(value);
   const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
   const isFlowActive = currentSavedFlow?.shared_status === "active";
   const isDefaultBranch = isBranch && index === 0;

   function setValue(value) {
      if (isBranch) {
         const { branches = [] } = { ...actionForm };
         const updatedBranches = branches.map((branch) => (branch.id === id ? { ...branch, [name]: value } : branch));
         setActionForm({
            ...actionForm,
            branches: updatedBranches
         });
         return;
      }
      setActionForm({
         ...actionForm,
         inputValue: { ...actionForm.inputValue, value: value }
      });
   }

   const handleChangeValue = (e, val) => {
      const valueToUse = val ? val : e.target.value;
      setFirstValue(valueToUse);
      debouncer(valueToUse);
   };

   const handleDefaultEnabledChange = (event) => {
      const updatedBranches = actionForm.branches.map((branch, index) =>
         index === 0
             ? {
                  ...branch,
                  isDefaultEnabled: event.target.checked
               }
            : branch
      );

      setActionForm({
         ...actionForm,
         branches: updatedBranches
      });
   };

   const handleDeleteBranch = (branch) => (event) => {
      event.stopPropagation();

      if (actionForm.branches.length <= 2) return;

      const deletedNodesAndEdgesIndexes = tryCatchWrapper(deleteBranchFromCanvas)(branch);
      const deletedBranches = tryCatchWrapper(deleteBranchFromActionForm)(branch);

      setRemoveBranchData((prev) => ({
         nodes: [...prev.nodes, deletedNodesAndEdgesIndexes.nodesWithIndex],
         edges: [...prev.edges, deletedNodesAndEdgesIndexes.edgesWithIndex],
         branches: deletedBranches
      }));
   };

   const getAllEdgesOfNodes = (nodes) => {
      const edges = getEdges();
      const nodeIdSet = new Set(nodes.map((node) => node.id));

      return edges.filter((edge) => nodeIdSet.has(edge.source) || nodeIdSet.has(edge.target));
   };

   const deleteBranchFromCanvas = (branch) => {
      const mergeNodeId = branch.endPoint;
      const allNodes = getNodes();
      const allEdges = getEdges();

      const outgoerNodes = tryCatchWrapper(getNodeOutgoers)(getNode(branch.id), mergeNodeId, []);
      const idsToDelete = [{ id: branch.id }, ...outgoerNodes.map((o) => ({ id: o.id }))];
      const nodes = idsToDelete.map(({ id }) => getNode(id)).filter(Boolean);
      const edges = getAllEdgesOfNodes(nodes);

      const nodesWithIndex = nodes.map((node) => ({
         node,
         index: allNodes.findIndex((n) => n.id === node.id)
      }));

      const edgesWithIndex = edges.map((edge) => ({
         edge,
         index: allEdges.findIndex((e) => e.id === edge.id)
      }));

      deleteElements({ nodes: idsToDelete });
      handleFitView();
      setTimeout(() => {
         updateSelectionNodeBranchesField(branch);
      }, 1000);

      return {
         nodesWithIndex,
         edgesWithIndex
      };
   };

   const updateSelectionNodeBranchesField = (branch) => {
      const nodes = getNodes();
      const parentNode = nodes.find((node) => node.id === branch.startPoint);
      if (parentNode && parentNode.data && parentNode.data.flowElement && parentNode.data.flowElement.data) {
         parentNode.data.flowElement.data.branches = parentNode.data.flowElement.data.branches?.filter((b) => b.id !== branch.id);
         setNodes([...nodes]);
      }
   };

   const deleteBranchFromActionForm = (branch) => {
      const actionFormCopy = { ...actionForm };
      const deletedBranches = actionFormCopy.branches?.filter((b) => b.id === branch.id) ?? [];
      actionFormCopy.branches = actionFormCopy.branches?.filter((b) => b.id !== branch.id);
      setActionForm(actionFormCopy);
      return deletedBranches;
   };

   const getNodeOutgoers = (node, mergeId, outgoerNodes = []) => {
      const edgesWithoutFloating = getEdges().filter((edge) => edge.type !== EDGE_TYPES.FLOATING);
      const outgoers = getOutgoers(node, getNodes(), edgesWithoutFloating);

      if (outgoers.length === 0) {
         return outgoerNodes;
      }

      const mergeNode = outgoers.find((outgoer) => outgoer.id === mergeId);

      if (mergeNode) {
         return outgoerNodes;
      }

      let accumulated = [...outgoerNodes, ...outgoers];

      outgoers.forEach((outgoer) => {
         accumulated = getNodeOutgoers(outgoer, mergeId, accumulated);
      });

      return accumulated;
   };

   return (
      <ValueInputWrapper>
         {isDefaultBranch ? (
            <SoftBox sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
               <SoftTypography sx={{ mb: 0.75, fontSize: "13px", lineHeight: 1.4375 }}>
                  {label}
               </SoftTypography>
               <SoftBox
                  component="label"
                  htmlFor={`selection-default-${id}`}
                  sx={{
                     display: "inline-flex",
                     alignItems: "center",
                     alignSelf: "flex-start",
                     gap: 0.75,
                     minHeight: 32,
                     cursor: isFlowActive || !allowEditWorkflow ? "default" : "pointer"
                  }}
               >
                  <Checkbox
                     id={`selection-default-${id}`}
                     checked={!!field.isDefaultEnabled}
                     disabled={isFlowActive || !allowEditWorkflow}
                     onChange={handleDefaultEnabledChange}
                     className="flow-designer-cbx"
                     sx={{ p: 0.5, mt: "0 !important", flexShrink: 0 }}
                  />
                  <SoftTypography sx={{ fontSize: "13px", lineHeight: 1.4375 }}>
                     Use when no other branch matches the input value
                  </SoftTypography>
               </SoftBox>
            </SoftBox>
         ) : (
            <AutocompleteInput action={{ field: key, placeholder: label, label: label }} fieldValue={firstValue} onChange={handleChangeValue} isBranchInput={isBranch} disabled={isFlowActive || !allowEditWorkflow} />
         )}

         {isBranch && !isDefaultBranch && index !== 0 && !isFlowActive && (
            <ListItemIcon disabled={actionForm.branches.length <= 2} className="delete-variable-icon-wrapper">
               <ConditionalWrapper
                  condition={actionForm.branches.length > 2}
                  wrapper={(children) => (
                     <Tooltip placement="left" title="Delete">
                        {children}
                     </Tooltip>
                  )}
               >
                  <IconButton disabled={actionForm.branches.length <= 2} className="delete-variable-icon" sx={{ position: "relative", top: "2px" }} onClick={handleDeleteBranch(field)} fontSize="medium">
                     <ThemedTrashIcon className="delete-variable-icon" fontSize="medium" />
                  </IconButton>
               </ConditionalWrapper>
            </ListItemIcon>
         )}
      </ValueInputWrapper>
   );
};

export default ValueInput;
